// Accessibility settings — Plan-AccessibilitySettings.md (v1).
//
// User-global (NOT workspace-scoped) preferences persisted in localStorage.
// The provider mounts outside all routed components so the CSS classes /
// inline styles apply to <html> on first paint, before any route resolves
// and before AuthContext starts fetching the user.
//
// Forward-compat: any missing key in the persisted blob falls back to the
// default — adding new prefs in a future ship won't break old users'
// localStorage. Future v2: extract the same controls into a floating widget
// (`<AccessibilityWidget />`) mounted at app root, sharing this provider.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type AccessibilityFontSize = 'small' | 'medium' | 'large' | 'xlarge';

export interface AccessibilityPrefs {
  fontSize: AccessibilityFontSize;
  highContrast: boolean;
  reduceMotion: boolean;
  dyslexicFont: boolean;
}

const STORAGE_KEY = 'prodagi-accessibility-settings';

const DEFAULTS: AccessibilityPrefs = {
  fontSize: 'medium',
  highContrast: false,
  reduceMotion: false,
  dyslexicFont: false,
};

const FONT_SIZE_PX: Record<AccessibilityFontSize, string> = {
  small: '14px',
  medium: '16px',
  large: '18px',
  xlarge: '20px',
};

interface AccessibilityContextValue {
  prefs: AccessibilityPrefs;
  setPref: <K extends keyof AccessibilityPrefs>(key: K, value: AccessibilityPrefs[K]) => void;
  reset: () => void;
}

const AccessibilityContext = createContext<AccessibilityContextValue | undefined>(undefined);

function readPersisted(): AccessibilityPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return DEFAULTS;
    // Forward-compat: spread DEFAULTS first so any missing key (added in a
    // future ship) falls back to the default rather than `undefined`.
    return {
      ...DEFAULTS,
      fontSize: ['small', 'medium', 'large', 'xlarge'].includes(parsed.fontSize)
        ? parsed.fontSize
        : DEFAULTS.fontSize,
      highContrast: typeof parsed.highContrast === 'boolean' ? parsed.highContrast : DEFAULTS.highContrast,
      reduceMotion: typeof parsed.reduceMotion === 'boolean' ? parsed.reduceMotion : DEFAULTS.reduceMotion,
      dyslexicFont: typeof parsed.dyslexicFont === 'boolean' ? parsed.dyslexicFont : DEFAULTS.dyslexicFont,
    };
  } catch {
    return DEFAULTS;
  }
}

function writePersisted(prefs: AccessibilityPrefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* quota / private mode — fail silent; in-memory state still drives the UI */
  }
}

function applyToDocument(prefs: AccessibilityPrefs) {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;

  // Inline font-size — Tailwind's rem units scale automatically off the
  // <html> font-size, so this single line resizes the whole UI.
  html.style.fontSize = FONT_SIZE_PX[prefs.fontSize];

  // CSS classes drive the rest. We toggle individually so multiple prefs
  // can stack (e.g. dyslexic font + high contrast).
  html.classList.toggle('hc', prefs.highContrast);
  html.classList.toggle('reduce-motion', prefs.reduceMotion);
  html.classList.toggle('dyslexic-font', prefs.dyslexicFont);
}

export const AccessibilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize state synchronously from localStorage so the very first render
  // applies the persisted prefs (no flash-of-unstyled accessibility).
  const [prefs, setPrefs] = useState<AccessibilityPrefs>(() => readPersisted());

  // Apply to <html> immediately, even before any effect runs, so first paint
  // already reflects user prefs. We do it inside a layoutEffect-equivalent —
  // useEffect is fine for our purposes since the initial state was loaded
  // synchronously from localStorage and React will commit the inline style
  // very quickly. The provider is mounted outside the routed components so
  // there is no flash for downstream pages.
  useEffect(() => {
    applyToDocument(prefs);
  }, [prefs]);

  // Persist on every change.
  useEffect(() => {
    writePersisted(prefs);
  }, [prefs]);

  const setPref = useCallback<AccessibilityContextValue['setPref']>((key, value) => {
    setPrefs(prev => ({ ...prev, [key]: value }));
  }, []);

  const reset = useCallback(() => {
    setPrefs(DEFAULTS);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* fail silent */ }
  }, []);

  const value = useMemo<AccessibilityContextValue>(
    () => ({ prefs, setPref, reset }),
    [prefs, setPref, reset],
  );

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
    </AccessibilityContext.Provider>
  );
};

export function useAccessibility(): AccessibilityContextValue {
  const ctx = useContext(AccessibilityContext);
  if (!ctx) {
    throw new Error('useAccessibility must be used within AccessibilityProvider');
  }
  return ctx;
}

export const ACCESSIBILITY_DEFAULTS = DEFAULTS;
